import { useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isAdminEmail } from '@/config/admin';

/**
 * Bảo vệ route chỉ dành cho admin.
 * - Loading   → hiện spinner
 * - Chưa login → redirect /login?from=<path>
 * - Đã login nhưng không phải admin → hiện trang 403
 * - Là admin → render <Outlet />
 */
export default function AdminRoute() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirected = useRef(false);

  const userIsAdmin = isAdminEmail(user?.email);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !user && !redirected.current) {
      redirected.current = true;
      const from = encodeURIComponent(location.pathname);
      navigate(`/login?from=${from}`, { replace: true });
    }
  }, [isLoading, user, navigate, location]);

  useEffect(() => {
    if (user) redirected.current = false;
  }, [user]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm text-gray-500 dark:text-gray-400">Đang kiểm tra quyền truy cập...</p>
      </div>
    );
  }

  // Not logged in (will redirect via useEffect above)
  if (!user) return null;

  // Logged in but not admin → show 403
  if (!userIsAdmin) {
    return <ForbiddenPage email={user.email ?? ''} />;
  }

  // Admin → render page
  return <Outlet />;
}

// ─── 403 Forbidden page ───────────────────────────────────────────────────────
function ForbiddenPage({ email }: { email: string }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-8">

        {/* Logo */}
        <Link
          to="/"
          className="text-2xl font-bold text-emerald-400 hover:text-emerald-300 transition-all inline-block cursor-pointer"
          style={{ fontFamily: 'Pacifico, serif' }}
        >
          123Trading
        </Link>

        {/* Icon */}
        <div className="relative inline-flex">
          <div className="w-24 h-24 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto">
            <i className="ri-shield-cross-line text-5xl text-red-500"></i>
          </div>
          <div className="absolute -top-1 -right-1 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
            <i className="ri-close-line text-white text-sm font-bold"></i>
          </div>
        </div>

        {/* Message */}
        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Truy cập bị từ chối
          </h1>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            Tài khoản{' '}
            <span className="font-semibold text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
              {email}
            </span>{' '}
            không có quyền truy cập trang quản trị.
          </p>
        </div>

        {/* Detail box */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 text-left">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-amber-500 text-lg"></i>
            </div>
            <div className="text-sm text-amber-800 dark:text-amber-300 space-y-1">
              <p className="font-semibold">Trang này chỉ dành cho Admin</p>
              <p>Nếu bạn cho rằng đây là nhầm lẫn, hãy liên hệ quản trị viên để được cấp quyền.</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Link
            to="/"
            className="w-full inline-flex items-center justify-center py-3 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm transition-all duration-200 hover:scale-[1.02] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line mr-2"></i>
            Về trang chủ
          </Link>
          <Link
            to="/profile"
            className="w-full inline-flex items-center justify-center py-3 px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-line mr-2"></i>
            Xem hồ sơ cá nhân
          </Link>
        </div>

        {/* HTTP code watermark */}
        <p className="text-6xl font-black text-gray-100 dark:text-gray-800 select-none -mt-2">
          403
        </p>
      </div>
    </div>
  );
}